/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import styles from '../../../styles/components/pageComponents/program/ProgramForm.module.css';
import SearchableDropdown from '../../controlVariations/SearchableDropdown';
import MultiSelectDropdown from '../../controlVariations/MultiSelectDropdown';
import SearchableMultiSelectDropdown from '../../controlVariations/SearchableMultiselectDropdown';
import { useCreateCourse, useUploadCover } from '../../../hooks/useCourse';
import { CreateCourseRequest } from '../../../types/course';
import { useSolutions } from '../../../hooks/useCourse';
import { useUsers } from '../../../hooks/useAllUsers';
import SuccessModal from '../../controlVariations/ModularSuccessModal';
import ResponsiveModal from '../../controlVariations/ResponsiveModal';
import ErrorPopupModal from '../../controlVariations/ErrorPopupMoadal';
import AddSolution from './AddSolution';

type FieldType = 'text' | 'select' | 'textarea' | 'image-upload';

interface FieldConfig {
	id: string;
	label: string;
	subLabel?: string;
	type: FieldType;
	placeholder?: string;
	options?: string[];
	maxLength?: number;
	fullWidth?: boolean;
}

interface TabConfig {
	id: string;
	label: string;
	fields: FieldConfig[];
}

interface ProgramFormProps {
	onBack: () => void;
	onSuccess?: (course: any) => void;
}

// Added interfaces for Content Structure state
interface SubSectionData {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
	timezone: string;
	description: string;
}

interface SectionData {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
	timezone: string;
	description: string;
	subSections: SubSectionData[];
}

const TABS_CONFIG: TabConfig[] = [
	{
		id: 'program-info',
		label: 'Program Info',
		fields: [
			{
				id: 'solution',
				label: 'Offering Category',
				type: 'select',
				options: [],
			},
			{ id: 'cover-photo', label: '', type: 'image-upload' },
			{
				id: 'title',
				label: 'Program Title',
				type: 'text',
				placeholder: 'Program Title',
				maxLength: 60,
			},
			{
				id: 'description',
				label: 'Description',
				type: 'textarea',
				placeholder: 'Program details (Optional)',
				maxLength: 500,
				fullWidth: true,
			},
		],
	},
	{
		id: 'owner-and-faculty',
		label: 'Owner & Faculty',
		fields: [
			{
				id: 'owner',
				label: 'Program Owner',
				subLabel: 'Program Owner will have edit access to the program',
				type: 'select',
				placeholder: 'Select Coach',
				options: ['Coach A', 'Coach B'],
			},
			{
				id: 'faculty',
				label: 'Program Faculty',
				subLabel: 'Coaches contributing to the program',
				type: 'select',
				placeholder: 'Select Coach',
				options: ['Coach C', 'Coach D'],
			},
			{
				id: 'format',
				label: 'Program Format',
				subLabel: 'Number of activities and events',
				type: 'text',
				placeholder: 'Add Program Format',
			},
			{
				id: 'certification',
				label: 'Certification',
				subLabel: 'Details of certification',
				type: 'text',
				placeholder:
					'ICF Level 1 accredited Executive Coaching Certification',
			},
		],
	},
	{ id: 'learnings-and-takeaway', label: 'Learnings & Takeaway', fields: [] },
	// { id: 'content-structure', label: 'Content Structure', fields: [] },
	// { id: 'resources', label: 'Resources and Assignments', fields: [] },
	// { id: 'faqs', label: 'FAQs', fields: [] },
];

// Reusable SVG Components
const TrashIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="22"
		viewBox="0 0 20 22"
		fill="none"
	>
		<path
			d="M7.75 9.75V15.75M11.75 9.75V15.75M16.75 4.75V18.75C16.75 19.2804 16.5393 19.7891 16.1642 20.1642C15.7891 20.5393 15.2804 20.75 14.75 20.75H4.75C4.21957 20.75 3.71086 20.5393 3.33579 20.1642C2.96071 19.7891 2.75 19.2804 2.75 18.75V4.75M0.75 4.75H18.75M5.75 4.75V2.75C5.75 2.21957 5.96071 1.71086 6.33579 1.33579C6.71086 0.960714 7.21957 0.75 7.75 0.75H11.75C12.2804 0.75 12.7891 0.960714 13.1642 1.33579C13.5393 1.71086 13.75 2.21957 13.75 2.75V4.75"
			stroke="#6B7280"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const ChevronUpIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="#6B7280"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<polyline points="18 15 12 9 6 15"></polyline>
	</svg>
);

const ClockIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="#1E1E1E"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="12" cy="12" r="10"></circle>
		<polyline points="12 6 12 12 16 14"></polyline>
	</svg>
);

const ProgramForm: React.FC<ProgramFormProps> = ({ onBack, onSuccess }) => {
	const today = new Date().toISOString().split('T')[0];
	const [activeTabId, setActiveTabId] = useState(TABS_CONFIG[0].id);
	const [newSolutionTitle, setNewSolutionTitle] = useState('');
	const [formData, setFormData] = useState<Record<string, string>>({});

	// State for Tab 3 (Dynamic lists)
	const [programIncludes, setProgramIncludes] = useState<string[]>([
		'',
		'',
		'',
		'',
		'',
	]);
	const [takeaways, setTakeaways] = useState<string[]>(['', '', '', '', '']);

	//cover photo
	const [coverFile, setCoverFile] = useState<File | null>(null);
	const [coverPreview, setCoverPreview] = useState<string | null>(null);

	//errors
	const [errors, setErrors] = useState<Record<string, string>>({});

	const [selectedFaculty, setSelectedFaculty] = useState<string[]>([]);

	const [errorModal, setErrorModal] = useState<{
		open: boolean;
		message: string;
	}>({
		open: false,
		message: '',
	});

	const showError = (message: string) => {
		setErrorModal({
			open: true,
			message,
		});
	};

	const [showSuccessModal, setShowSuccessModal] = useState(false);

	const [successModal, setSuccessModal] = useState<{
		open: boolean;
		title: string;
		subtitle: string;
	}>({
		open: false,
		title: '',
		subtitle: '',
	});
	const showSuccess = (title: string, subtitle: string) => {
		setSuccessModal({
			open: true,
			title,
			subtitle,
		});

		setTimeout(() => {
			setSuccessModal(prev => ({ ...prev, open: false }));
		}, 3000);
	};

	const validateStep = () => {
		const newErrors: Record<string, string> = {};

		if (activeTabId === 'program-info') {
			if (!formData['solution']) {
				newErrors.solution = 'Offering category is required';
			}

			if (!formData['title']) {
				newErrors.title = 'Program title is required';
			} else if (formData['title'].trim().length < 5) {
				newErrors.title = 'Program title must be at least 5 characters';
			}

			if (
				formData['description'] &&
				formData['description'].trim().length < 20
			) {
				newErrors.description =
					'Description must be at least 20 characters';
			}

			if (!formData['startDate']) {
				newErrors.startDate = 'Start date is required';
			}

			if (!formData['endDate']) {
				newErrors.endDate = 'End date is required';
			}

			if (formData['startDate'] && formData['endDate']) {
				if (
					new Date(formData['endDate']) <
					new Date(formData['startDate'])
				) {
					newErrors.dateRange =
						'End date cannot be before start date';
				}
			}
		}

		if (activeTabId === 'owner-and-faculty') {
			if (!formData['owner']) {
				newErrors.owner = 'Program owner is required';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleCoverSelect = (file: File) => {
		setCoverFile(file);

		// preview
		const previewUrl = URL.createObjectURL(file);
		setCoverPreview(previewUrl);
	};

	// State for Tab 4 (Content Structure)
	const [sections, setSections] = useState<SectionData[]>([
		{
			id: 'sec-1',
			name: '',
			startTime: '15:00',
			endTime: '17:00',
			timezone: 'IST',
			description: '',
			subSections: [
				{
					id: 'subsec-1',
					name: '',
					startTime: '15:00',
					endTime: '17:00',
					timezone: 'IST',
					description: '',
				},
			],
		},
		{
			id: 'sec-2',
			name: '',
			startTime: '15:00',
			endTime: '17:00',
			timezone: 'IST',
			description: '',
			subSections: [],
		},
	]);

	const activeTab =
		TABS_CONFIG.find(t => t.id === activeTabId) || TABS_CONFIG[0];
	const activeTabIndex = TABS_CONFIG.findIndex(t => t.id === activeTabId);

	const handleInputChange = (id: string, value: string) => {
		setFormData(prev => ({ ...prev, [id]: value }));

		setErrors(prev => {
			const newErrors = { ...prev };

			// 🔥 TITLE VALIDATION (optional but consistent)
			if (id === 'title') {
				if (!value.trim()) {
					newErrors.title = 'Program title is required';
				} else if (value.trim().length < 5) {
					newErrors.title =
						'Program title must be at least 5 characters';
				} else {
					delete newErrors.title;
				}
			}

			// 🔥 DESCRIPTION LIVE VALIDATION
			if (id === 'description') {
				if (
					value &&
					value.trim().length > 0 &&
					value.trim().length < 20
				) {
					newErrors.description =
						'Description must be at least 20 characters';
				} else {
					delete newErrors.description;
				}
			}

			return newErrors;
		});
	};

	const handleNext = () => {
		const isValid = validateStep();

		if (!isValid) return;

		if (activeTabIndex < TABS_CONFIG.length - 1) {
			setActiveTabId(TABS_CONFIG[activeTabIndex + 1].id);
		}
	};

	const handleBack = () => {
		if (activeTabIndex > 0) {
			setActiveTabId(TABS_CONFIG[activeTabIndex - 1].id);
		} else {
			onBack();
		}
	};

	// Tab 3 Handlers
	const updateArrayItem = (
		setter: React.Dispatch<React.SetStateAction<string[]>>,
		index: number,
		value: string
	) => {
		setter(prev => {
			const newArr = [...prev];
			newArr[index] = value;
			return newArr;
		});
	};
	const addArrayItem = (
		setter: React.Dispatch<React.SetStateAction<string[]>>
	) => setter(prev => [...prev, '']);
	const removeArrayItem = (
		setter: React.Dispatch<React.SetStateAction<string[]>>,
		index: number
	) => setter(prev => prev.filter((_, i) => i !== index));

	// Tab 4 (Content Structure) Handlers
	const handleAddSection = () => {
		setSections([
			...sections,
			{
				id: `sec-${Date.now()}`,
				name: '',
				startTime: '15:00',
				endTime: '17:00',
				timezone: 'IST',
				description: '',
				subSections: [],
			},
		]);
	};
	const handleRemoveSection = (id: string) =>
		setSections(sections.filter(s => s.id !== id));
	const handleUpdateSection = (
		id: string,
		field: keyof SectionData,
		value: string
	) => {
		setSections(
			sections.map(s => (s.id === id ? { ...s, [field]: value } : s))
		);
	};

	const handleAddSubSection = (sectionId: string) => {
		setSections(
			sections.map(s =>
				s.id === sectionId
					? {
							...s,
							subSections: [
								...s.subSections,
								{
									id: `subsec-${Date.now()}`,
									name: '',
									startTime: '15:00',
									endTime: '17:00',
									timezone: 'IST',
									description: '',
								},
							],
						}
					: s
			)
		);
	};
	const handleRemoveSubSection = (sectionId: string, subId: string) => {
		setSections(
			sections.map(s =>
				s.id === sectionId
					? {
							...s,
							subSections: s.subSections.filter(
								sub => sub.id !== subId
							),
						}
					: s
			)
		);
	};
	const handleUpdateSubSection = (
		sectionId: string,
		subId: string,
		field: keyof SubSectionData,
		value: string
	) => {
		setSections(
			sections.map(s =>
				s.id === sectionId
					? {
							...s,
							subSections: s.subSections.map(sub =>
								sub.id === subId
									? { ...sub, [field]: value }
									: sub
							),
						}
					: s
			)
		);
	};

	const [isAddSolutionOpen, setIsAddSolutionOpen] = useState(false);

	const {
		solutions,
		createSolution,
		isLoading: solutionsLoading,
	} = useSolutions();

	const handleAddSolutionClick = () => {
		setIsAddSolutionOpen(true);
	};

	const handleCreateSolution = async (solutionName: string) => {
		try {
			const newSolution = await createSolution({ title: solutionName });

			// auto-select newly created solution
			setFormData(prev => ({
				...prev,
				solution: newSolution.id,
			}));

			setIsAddSolutionOpen(false);
		} catch (err) {
			console.error(err);
			showError('Failed to create Offering');
		}
	};

	const { createCourse, isLoading } = useCreateCourse();

	const {
		coaches,
		admins,
		fetchCoaches,
		fetchAdminAndSuperAdmins,
		loading: usersLoading,
	} = useUsers();

	useEffect(() => {
		fetchCoaches('coach');
		fetchAdminAndSuperAdmins();
	}, []);

	const ownerOptions = [
		...admins.map(u => ({
			label: `${u.fullname} (${u.rolename})`,
			value: u.id,
		})),
	];

	const facultyOptions = [
		...coaches.map(u => ({
			label: `${u.fullname}`,
			value: u.id,
		})),
	];

	const allUsersOptions = [
		...admins.map(u => {
			const role = u.rolename === 'superadmin' ? 'admin' : u.rolename;

			return {
				label: `${u.fullname} (${role})`,
				value: u.id,
			};
		}),

		...coaches.map(u => {
			const role =
				u.rolename === 'superadmin' ? 'admin' : u.rolename || 'coach';

			return {
				label: `${u.fullname} (${role})`,
				value: u.id,
			};
		}),
	];

	const ownerLabels = ownerOptions.map(o => o.label);
	const facultyLabels = facultyOptions.map(o => o.label);
	const allUserLabels = allUsersOptions.map(u => u.label);

	const toISODate = (date?: string) => {
		if (!date) return undefined;
		return new Date(date).toISOString();
	};

	const buildPayload = (
		status: 'draft' | 'published'
	): CreateCourseRequest => {
		return {
			title: formData['title'] || '',
			description: formData['description'] || '',

			coursetype: 'standard',
			deliverymode: 'hybrid',
			difficulty: 'beginner',

			category: formData['category'] || 'standard',
			solutionid: formData['solution'],

			programincludes: programIncludes.filter(Boolean),
			learningtakeaways: takeaways.filter(Boolean),

			programformat: formData['format'],
			certification: formData['certification'],

			facultyids: selectedFaculty.map((id, index) => ({
				userid: id,
				facultyrole: 'faculty',
				displayorder: index,
			})),

			programownerid: formData['owner'],
			// 🔥 NEW
			status,
			ispublic: status === 'published',

			startdate: toISODate(formData['startDate']),
			enddate: toISODate(formData['endDate']),
		};
	};

	const { uploadCover } = useUploadCover();

	const handleSaveDraft = async () => {
		try {
			const payload = buildPayload('draft');

			const createdCourse = await createCourse(payload);

			if (coverFile && createdCourse?.id) {
				await uploadCover(createdCourse.id, coverFile);
			}

			// ✅ ADD THIS
			if (createdCourse) {
				onSuccess?.(createdCourse);
			}

			showSuccess(
				'Draft Saved',
				'Your program draft has been saved successfully.'
			);

			setTimeout(() => {
				onBack();
			}, 2000);
		} catch (err: any) {
			console.error(err);

			const message =
				err?.response?.data?.message ||
				err?.message ||
				'Something went wrong while saving draft';

			showError(message);
		}
	};

	const handlePublish = async () => {
		try {
			const payload = buildPayload('published');

			const createdCourse = await createCourse(payload);

			if (coverFile && createdCourse?.id) {
				await uploadCover(createdCourse.id, coverFile);
			}

			showSuccess(
				'New Program Published',
				`${createdCourse?.title || 'New Program'} has been published.` +
					'View the program details page and add more details'
			);
			// onBack();

			if (createdCourse) {
				onSuccess?.(createdCourse); // 🔥 send data back
			}

			setTimeout(() => {
				onBack();
			}, 2000);
		} catch (err: any) {
			console.error(err);

			const message =
				err?.response?.data?.message ||
				err?.message ||
				'Failed to publish program';

			showError(message);
		}
	};

	const isNextDisabled = () => {
		if (activeTabId === 'program-info') {
			return (
				!formData['solution'] ||
				!formData['title'] ||
				formData['title'].trim().length < 5 ||
				(formData['description'] &&
					formData['description'].trim().length < 20)
			);
		}

		if (activeTabId === 'owner-and-faculty') {
			return !formData['owner'];
		}

		return false;
	};
	return (
		<div className={styles.wrapper}>
			{/* Top Header */}
			<div className={styles.topActions}>
				<button className={styles.backBtn} onClick={onBack}>
					← Back
				</button>
				<div className={styles.rightGroup}>
					<button
						className={styles.btnDraft}
						onClick={handleSaveDraft}
					>
						Save as draft
					</button>
				</div>
			</div>

			{/* Tabs */}
			<div className={styles.tabContainer}>
				{TABS_CONFIG.map(tab => (
					<button
						key={tab.id}
						className={`${styles.tabItem} ${activeTabId === tab.id ? styles.activeTab : ''}`}
						onClick={() => {}}
						style={{ cursor: 'not-allowed' }}
					>
						{tab.label}
					</button>
				))}
			</div>

			<div className={styles.formBody}>
				{/* TAB 1: PROGRAM INFO */}
				{activeTabId === 'program-info' && (
					<div className={styles.fieldGrid}>
						{activeTab.fields.map(field => (
							<div
								key={field.id}
								className={`${styles.fieldWrapper} ${field.fullWidth ? styles.fullWidth : ''} ${field.type === 'image-upload' ? styles.imageUploadArea : ''}`}
							>
								{field.type !== 'image-upload' && (
									<div className={styles.labelRow}>
										<label className={styles.label}>
											{field.label}
											{['solution', 'title'].includes(
												field.id
											) && (
												<span
													className={styles.required}
												>
													{' '}
													*
												</span>
											)}
											{field.id === 'solution' && (
												<span
													className={styles.addLink}
													onClick={
														handleAddSolutionClick
													}
												>
													Add New Offering +
												</span>
											)}
										</label>
										{field.maxLength && (
											<span className={styles.counter}>
												{
													(formData[field.id] || '')
														.length
												}
												/{field.maxLength}
											</span>
										)}
									</div>
								)}

								{/* FIELD */}
								{field.id === 'solution' && (
									<SearchableDropdown
										placeholder="Select Offering"
										options={solutions.map(
											sol => sol.title
										)}
										selected={
											solutions.find(
												sol =>
													sol.id ===
													formData['solution']
											)?.title
										}
										onSelect={value => {
											const selectedSol = solutions.find(
												sol => sol.title === value
											);
											if (selectedSol) {
												handleInputChange(
													'solution',
													selectedSol.id
												);
											}
										}}
									/>
								)}

								{field.type === 'textarea' && (
									<textarea
										className={styles.textarea}
										placeholder={field.placeholder}
										maxLength={field.maxLength}
										value={formData[field.id] || ''}
										onChange={e =>
											handleInputChange(
												field.id,
												e.target.value
											)
										}
									/>
								)}

								{field.type === 'image-upload' && (
									<div
										className={styles.photoBox}
										onClick={() =>
											document
												.getElementById('coverInput')
												?.click()
										}
									>
										{coverPreview ? (
											<img
												src={coverPreview}
												alt="cover"
												className={styles.coverPreview}
											/>
										) : (
											<button
												className={styles.uploadBtn}
											>
												Add Cover Photo
											</button>
										)}

										<input
											id="coverInput"
											type="file"
											accept="image/*"
											style={{ display: 'none' }}
											onChange={e => {
												const file =
													e.target.files?.[0];
												if (file)
													handleCoverSelect(file);
											}}
										/>
									</div>
								)}

								{field.type === 'text' && (
									<input
										type="text"
										className={styles.input}
										placeholder={field.placeholder}
										maxLength={field.maxLength}
										value={formData[field.id] || ''}
										onChange={e =>
											handleInputChange(
												field.id,
												e.target.value
											)
										}
									/>
								)}

								{errors[field.id] && (
									<span className={styles.errorText}>
										{errors[field.id]}
									</span>
								)}
								{field.id === 'title' && (
									<div className={styles.formGroup}>
										<label className={styles.label}>
											Date{' '}
											<span className={styles.required}>
												*
											</span>
										</label>

										<div className={styles.dateContainer}>
											<div
												className={styles.calendarIcon}
											>
												<svg
													width="20"
													height="20"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
												>
													<rect
														x="3"
														y="4"
														width="18"
														height="18"
														rx="2"
														ry="2"
													></rect>
													<line
														x1="16"
														y1="2"
														x2="16"
														y2="6"
													></line>
													<line
														x1="8"
														y1="2"
														x2="8"
														y2="6"
													></line>
													<line
														x1="3"
														y1="10"
														x2="21"
														y2="10"
													></line>
												</svg>
											</div>

											<input
												type="date"
												min={today}
												required
												className={`${styles.dateInput} ${errors.startDate || errors.dateRange ? styles.errorInput : ''}`}
												value={formData.startDate || ''}
												onChange={e =>
													handleInputChange(
														'startDate',
														e.target.value
													)
												}
											/>

											<span
												className={styles.dateSeparator}
											>
												to
											</span>

											<input
												type="date"
												min={
													formData.startDate || today
												}
												required
												className={`${styles.dateInput} ${errors.endDate || errors.dateRange ? styles.errorInput : ''}`}
												value={formData.endDate || ''}
												onChange={e =>
													handleInputChange(
														'endDate',
														e.target.value
													)
												}
											/>
										</div>

										{(errors.startDate ||
											errors.endDate ||
											errors.dateRange) && (
											<span className={styles.errorText}>
												{errors.startDate ||
													errors.endDate ||
													errors.dateRange}
											</span>
										)}
									</div>
								)}
							</div>
						))}
					</div>
				)}

				{/* TAB 2: OWNER & FACULTY */}
				{activeTabId === 'owner-and-faculty' && (
					<div className={styles.infoLayoutContainer}>
						<div className={styles.descriptionColumn}>
							<div className={styles.infoTitle}>
								<span className={styles.infoIcon}>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="27"
										height="27"
										viewBox="0 0 27 27"
										fill="none"
									>
										<path
											d="M13.25 18.25V13.25M13.25 8.25H13.2625M25.75 13.25C25.75 20.1536 20.1536 25.75 13.25 25.75C6.34644 25.75 0.75 20.1536 0.75 13.25C0.75 6.34644 6.34644 0.75 13.25 0.75C20.1536 0.75 25.75 6.34644 25.75 13.25Z"
											stroke="#147B58"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</span>
								<div className={styles.titleDividerCont}>
									Info{' '}
									<span
										className={styles.titleDividerLine}
									></span>
								</div>
							</div>
							<div className={styles.titleDividerLine}></div>
							{activeTab.fields.map(field => (
								<div
									key={field.id}
									className={styles.textBlock}
								>
									<h4 className={styles.blockLabel}>
										{field.label}
									</h4>
									<p className={styles.blockSubLabel}>
										{field.subLabel}
									</p>
								</div>
							))}
						</div>

						<div className={styles.inputColumnShaded}>
							{activeTab.fields.map(field => (
								<div
									key={field.id}
									className={styles.shadedFieldRow}
								>
									<div className={styles.shadedLabelRow}>
										<span
											className={styles.iconPlaceholder}
										>
											{field.id === 'owner' ? (
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="27"
													height="27"
													viewBox="0 0 27 27"
													fill="none"
												>
													<path
														d="M20.75 23.25C20.75 21.2609 19.9598 19.3532 18.5533 17.9467C17.1468 16.5402 15.2391 15.75 13.25 15.75M13.25 15.75C11.2609 15.75 9.35322 16.5402 7.9467 17.9467C6.54018 19.3532 5.75 21.2609 5.75 23.25M13.25 15.75C16.0114 15.75 18.25 13.5114 18.25 10.75C18.25 7.98858 16.0114 5.75 13.25 5.75C10.4886 5.75 8.25 7.98858 8.25 10.75C8.25 13.5114 10.4886 15.75 13.25 15.75ZM25.75 13.25C25.75 20.1536 20.1536 25.75 13.25 25.75C6.34644 25.75 0.75 20.1536 0.75 13.25C0.75 6.34644 6.34644 0.75 13.25 0.75C20.1536 0.75 25.75 6.34644 25.75 13.25Z"
														stroke="#147B58"
														strokeWidth="1.5"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											) : field.id === 'faculty' ? (
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="22"
													height="21"
													viewBox="0 0 22 21"
													fill="none"
												>
													<path
														d="M0.75 18.75C0.749916 17.2103 1.19413 15.7033 2.02935 14.4099C2.86456 13.1164 4.05527 12.0915 5.4586 11.458C6.86193 10.8246 8.41824 10.6095 9.94076 10.8387C11.4633 11.0678 12.8873 11.7315 14.042 12.75M17.75 13.75V19.75M20.75 16.75H14.75M13.75 5.75C13.75 8.51142 11.5114 10.75 8.75 10.75C5.98858 10.75 3.75 8.51142 3.75 5.75C3.75 2.98858 5.98858 0.75 8.75 0.75C11.5114 0.75 13.75 2.98858 13.75 5.75Z"
														stroke="#147B58"
														strokeWidth="1.5"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											) : field.id === 'format' ? (
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="27"
													viewBox="0 0 24 27"
													fill="none"
												>
													<path
														d="M7 0.75V5.75M17 0.75V5.75M0.75 10.75H23.25M7 15.75H7.0125M12 15.75H12.0125M17 15.75H17.0125M7 20.75H7.0125M12 20.75H12.0125M17 20.75H17.0125M3.25 3.25H20.75C22.1307 3.25 23.25 4.36929 23.25 5.75V23.25C23.25 24.6307 22.1307 25.75 20.75 25.75H3.25C1.86929 25.75 0.75 24.6307 0.75 23.25V5.75C0.75 4.36929 1.86929 3.25 3.25 3.25Z"
														stroke="#147B58"
														strokeWidth="1.5"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											) : (
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="27"
													height="27"
													viewBox="0 0 27 27"
													fill="none"
												>
													<path
														d="M10.75 16.575V18.6075C10.7453 19.0358 10.6306 19.4557 10.417 19.8269C10.2033 20.1981 9.89792 20.5082 9.53 20.7275C8.74893 21.306 8.11355 22.0587 7.67436 22.9258C7.23516 23.7929 7.00426 24.7505 7 25.7225M15.75 16.575V18.6075C15.7547 19.0358 15.8694 19.4557 16.083 19.8269C16.2967 20.1981 16.6021 20.5082 16.97 20.7275C17.7511 21.306 18.3864 22.0587 18.8256 22.9258C19.2648 23.7929 19.4957 24.7505 19.5 25.7225M20.75 9.5H22.625C23.4538 9.5 24.2487 9.17076 24.8347 8.58471C25.4208 7.99866 25.75 7.2038 25.75 6.375C25.75 5.5462 25.4208 4.75134 24.8347 4.16529C24.2487 3.57924 23.4538 3.25 22.625 3.25H20.75M20.75 9.5C20.75 11.4891 19.9598 13.3968 18.5533 14.8033C17.1468 16.2098 15.2391 17 13.25 17C11.2609 17 9.35322 16.2098 7.9467 14.8033C6.54018 13.3968 5.75 11.4891 5.75 9.5M20.75 9.5V2C20.75 1.66848 20.6183 1.35054 20.3839 1.11612C20.1495 0.881696 19.8315 0.75 19.5 0.75H7C6.66848 0.75 6.35054 0.881696 6.11612 1.11612C5.8817 1.35054 5.75 1.66848 5.75 2V9.5M3.25 25.75H23.25M5.75 9.5H3.875C3.0462 9.5 2.25134 9.17076 1.66529 8.58471C1.07924 7.99866 0.75 7.2038 0.75 6.375C0.75 5.5462 1.07924 4.75134 1.66529 4.16529C2.25134 3.57924 3.0462 3.25 3.875 3.25H5.75"
														stroke="#147B58"
														strokeWidth="1.5"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											)}
										</span>
										<label>
											{field.label}
											{field.id === 'owner' && (
												<span
													className={styles.required}
												>
													{' '}
													*
												</span>
											)}
										</label>
										{/* <button className={styles.deleteBtn}><TrashIcon /></button> */}
									</div>
									{/* OWNER → Searchable */}
									{field.id === 'owner' && (
										<>
											<SearchableDropdown
												placeholder="Select Program Owner"
												options={allUserLabels}
												selected={
													allUsersOptions.find(
														o =>
															o.value ===
															formData['owner']
													)?.label
												}
												onSelect={label => {
													const selected =
														allUsersOptions.find(
															o =>
																o.label ===
																label
														);
													if (selected) {
														handleInputChange(
															'owner',
															selected.value
														);
													}
												}}
											/>

											{errors['owner'] && (
												<span
													className={styles.errorText}
												>
													{errors['owner']}
												</span>
											)}
										</>
									)}

									{/* FACULTY → Multi Select */}
									{field.id === 'faculty' && (
										<SearchableMultiSelectDropdown
											placeholder="Select Coaches"
											options={facultyLabels}
											selected={selectedFaculty.map(
												id =>
													facultyOptions.find(
														o => o.value === id
													)?.label || ''
											)}
											onChange={labels => {
												const ids = labels
													.map(
														label =>
															facultyOptions.find(
																o =>
																	o.label ===
																	label
															)?.value
													)
													.filter(
														Boolean
													) as string[];

												setSelectedFaculty(ids);
											}}
										/>
									)}

									{/* OTHER INPUTS */}
									{field.id !== 'owner' &&
										field.id !== 'faculty' && (
											<input
												type="text"
												className={styles.shadedInput}
												placeholder={field.placeholder}
												value={formData[field.id] || ''}
												onChange={e =>
													handleInputChange(
														field.id,
														e.target.value
													)
												}
											/>
										)}
								</div>
							))}
						</div>
					</div>
				)}

				{/* TAB 3: LEARNINGS & TAKEAWAY */}
				{activeTabId === 'learnings-and-takeaway' && (
					<div className={styles.twoColumnDynamicLayout}>
						{/* Program Includes Column */}
						<div className={styles.dynamicListCard}>
							<div className={styles.dynamicHeader}>
								<p className={styles.dynamicTitle}>
									Program Includes:
								</p>
								<div className={styles.titleDividerLine}></div>
							</div>
							<div className={styles.dynamicList}>
								{programIncludes.map((item, idx) => (
									<div
										key={idx}
										className={styles.dynamicInputRow}
									>
										<input
											type="text"
											className={styles.dynamicInput}
											placeholder="Program Detail"
											value={item}
											onChange={e =>
												updateArrayItem(
													setProgramIncludes,
													idx,
													e.target.value
												)
											}
										/>
										<button
											className={styles.deleteBtn}
											onClick={() =>
												removeArrayItem(
													setProgramIncludes,
													idx
												)
											}
										>
											<TrashIcon />
										</button>
									</div>
								))}
							</div>
							<button
								className={styles.addDynamicBtn}
								onClick={() => addArrayItem(setProgramIncludes)}
							>
								Add New Row +
							</button>
						</div>

						{/* Learnings & Takeaway Column */}
						<div className={styles.dynamicListCard}>
							<div className={styles.dynamicHeader}>
								<p className={styles.dynamicTitle}>
									Learnings & Takeaway:
								</p>
								<div className={styles.titleDividerLine}></div>
							</div>
							<div className={styles.dynamicList}>
								{takeaways.map((item, idx) => (
									<div
										key={idx}
										className={styles.dynamicInputRow}
									>
										<span>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="27"
												height="27"
												viewBox="0 0 27 27"
												fill="none"
											>
												<path
													d="M25.4983 10.7542C26.0692 13.5559 25.6623 16.4685 24.3456 19.0065C23.0289 21.5444 20.8819 23.5543 18.2627 24.7009C15.6435 25.8475 12.7103 26.0615 9.95244 25.3072C7.19453 24.5529 4.77856 22.876 3.10742 20.556C1.43627 18.236 0.610975 15.4133 0.769153 12.5585C0.92733 9.70367 2.05942 6.98937 3.97663 4.86822C5.89385 2.74707 8.48029 1.3473 11.3046 0.902326C14.129 0.457355 17.0205 0.994084 19.4971 2.42301M9.49706 12.0042L13.2471 15.7542L25.747 3.25426"
													stroke="#147B58"
													strokeWidth="1.5"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</span>
										<input
											type="text"
											className={styles.dynamicInput}
											placeholder="Key Takeaway"
											value={item}
											onChange={e =>
												updateArrayItem(
													setTakeaways,
													idx,
													e.target.value
												)
											}
										/>
										<button
											className={styles.deleteBtn}
											onClick={() =>
												removeArrayItem(
													setTakeaways,
													idx
												)
											}
										>
											<TrashIcon />
										</button>
									</div>
								))}
							</div>
							<button
								className={styles.addDynamicBtn}
								onClick={() => addArrayItem(setTakeaways)}
							>
								Add New Row +
							</button>
						</div>
					</div>
				)}

				{activeTabId === 'content-structure' && (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '20px',
						}}
					>
						{/* Header Area */}
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								paddingBottom: '10px',
							}}
						>
							<h3
								style={{
									fontSize: '18px',
									fontWeight: '600',
									color: '#1E1E1E',
									margin: 0,
								}}
							>
								Program Content
							</h3>
							<div
								style={{
									fontSize: '14px',
									color: '#1E1E1E',
									display: 'flex',
									gap: '15px',
									fontWeight: '500',
								}}
							>
								<span>{sections.length} Sections</span> •{' '}
								<span>15 Lectures</span> •{' '}
								<span>8h 12m Total Duration</span>
							</div>
						</div>

						{/* Sections List */}
						{sections.map((section, sIdx) => (
							<div
								key={section.id}
								style={{
									border: '1px solid #E5E7EB',
									borderRadius: '8px',
									padding: '24px',
									backgroundColor: '#FAFAFB',
									display: 'flex',
									flexDirection: 'column',
									gap: '20px',
								}}
							>
								{/* Section Header */}
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
									}}
								>
									<span
										style={{
											fontSize: '16px',
											fontWeight: '500',
											color: '#1E1E1E',
										}}
									>
										Section {sIdx + 1}
									</span>
									<div
										style={{ display: 'flex', gap: '12px' }}
									>
										{/* <button className={styles.deleteBtn} onClick={() => handleRemoveSection(section.id)}><TrashIcon /></button> */}
										<button className={styles.deleteBtn}>
											<ChevronUpIcon />
										</button>
									</div>
								</div>

								{/* Section Fields: Name & Duration */}
								<div
									style={{
										display: 'flex',
										gap: '20px',
										alignItems: 'flex-start',
									}}
								>
									<div style={{ flex: 1 }}>
										<label
											className={styles.label}
											style={{
												marginBottom: '8px',
												display: 'block',
											}}
										>
											Section Name
										</label>
										<input
											className={styles.input}
											placeholder="Masterclass/Session"
											value={section.name}
											onChange={e =>
												handleUpdateSection(
													section.id,
													'name',
													e.target.value
												)
											}
										/>
									</div>
									<div>
										<label
											className={styles.label}
											style={{
												marginBottom: '8px',
												display: 'block',
											}}
										>
											Duration
										</label>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '10px',
											}}
										>
											<ClockIcon />
											<input
												type="time"
												className={styles.input}
												style={{
													width: '100px',
													textAlign: 'center',
												}}
												value={section.startTime}
												onChange={e =>
													handleUpdateSection(
														section.id,
														'startTime',
														e.target.value
													)
												}
											/>
											<span
												style={{
													color: '#6B7280',
													fontSize: '14px',
												}}
											>
												to
											</span>
											<input
												type="time"
												className={styles.input}
												style={{
													width: '100px',
													textAlign: 'center',
												}}
												value={section.endTime}
												onChange={e =>
													handleUpdateSection(
														section.id,
														'endTime',
														e.target.value
													)
												}
											/>
											<select
												className={styles.input}
												style={{ width: '90px' }}
												value={section.timezone}
												onChange={e =>
													handleUpdateSection(
														section.id,
														'timezone',
														e.target.value
													)
												}
											>
												<option value="IST">IST</option>
												<option value="EST">EST</option>
												<option value="PST">PST</option>
											</select>
										</div>
									</div>
								</div>

								{/* Section Description */}
								<div>
									<div
										className={styles.labelRow}
										style={{ marginBottom: '8px' }}
									>
										<label className={styles.label}>
											Description
										</label>
										<span className={styles.counter}>
											{section.description.length}/500
										</span>
									</div>
									<textarea
										className={styles.textarea}
										placeholder="Enter details (Optional)"
										maxLength={500}
										value={section.description}
										onChange={e =>
											handleUpdateSection(
												section.id,
												'description',
												e.target.value
											)
										}
										style={{ minHeight: '80px' }}
									/>
								</div>

								{/* Sub-Sections List */}
								{section.subSections.map((sub, ssIdx) => (
									<div
										key={sub.id}
										style={{
											border: '1px solid #E5E7EB',
											borderRadius: '8px',
											padding: '20px',
											backgroundColor: '#FFFFFF',
											display: 'flex',
											flexDirection: 'column',
											gap: '20px',
											marginTop: '10px',
										}}
									>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
											}}
										>
											<span
												style={{
													fontSize: '15px',
													fontWeight: '500',
													color: '#1E1E1E',
												}}
											>
												Sub-Section{' '}
												{String.fromCharCode(
													65 + ssIdx
												)}
											</span>
											<div
												style={{
													display: 'flex',
													gap: '12px',
												}}
											>
												{/* <button className={styles.deleteBtn} onClick={() => handleRemoveSubSection(section.id, sub.id)}><TrashIcon /></button> */}
												<button
													className={styles.deleteBtn}
												>
													<ChevronUpIcon />
												</button>
											</div>
										</div>

										<div
											style={{
												display: 'flex',
												gap: '20px',
												alignItems: 'flex-start',
											}}
										>
											<div style={{ flex: 1 }}>
												<label
													className={styles.label}
													style={{
														marginBottom: '8px',
														display: 'block',
													}}
												>
													Sub-Section Name
												</label>
												<input
													className={styles.input}
													placeholder="Coach Education"
													value={sub.name}
													onChange={e =>
														handleUpdateSubSection(
															section.id,
															sub.id,
															'name',
															e.target.value
														)
													}
												/>
											</div>
											<div>
												<label
													className={styles.label}
													style={{
														marginBottom: '8px',
														display: 'block',
													}}
												>
													Duration
												</label>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '10px',
													}}
												>
													<ClockIcon />
													<input
														type="time"
														className={styles.input}
														style={{
															width: '100px',
															textAlign: 'center',
														}}
														value={sub.startTime}
														onChange={e =>
															handleUpdateSubSection(
																section.id,
																sub.id,
																'startTime',
																e.target.value
															)
														}
													/>
													<span
														style={{
															color: '#6B7280',
															fontSize: '14px',
														}}
													>
														to
													</span>
													<input
														type="time"
														className={styles.input}
														style={{
															width: '100px',
															textAlign: 'center',
														}}
														value={sub.endTime}
														onChange={e =>
															handleUpdateSubSection(
																section.id,
																sub.id,
																'endTime',
																e.target.value
															)
														}
													/>
													<select
														className={styles.input}
														style={{
															width: '90px',
														}}
														value={sub.timezone}
														onChange={e =>
															handleUpdateSubSection(
																section.id,
																sub.id,
																'timezone',
																e.target.value
															)
														}
													>
														<option value="IST">
															IST
														</option>
														<option value="EST">
															EST
														</option>
														<option value="PST">
															PST
														</option>
													</select>
												</div>
											</div>
										</div>

										<div>
											<div
												className={styles.labelRow}
												style={{ marginBottom: '8px' }}
											>
												<label className={styles.label}>
													Description
												</label>
												<span
													className={styles.counter}
												>
													{sub.description.length}/500
												</span>
											</div>
											<textarea
												className={styles.textarea}
												placeholder="Enter details (Optional)"
												maxLength={500}
												value={sub.description}
												onChange={e =>
													handleUpdateSubSection(
														section.id,
														sub.id,
														'description',
														e.target.value
													)
												}
												style={{ minHeight: '80px' }}
											/>
										</div>
									</div>
								))}

								{/* Add Sub-Section Trigger */}
								<button
									style={{
										background: 'none',
										border: 'none',
										color: '#147B58',
										fontWeight: '500',
										fontSize: '15px',
										cursor: 'pointer',
										alignSelf: 'flex-start',
										padding: 0,
									}}
									onClick={() =>
										handleAddSubSection(section.id)
									}
								>
									Add Sub-Section +
								</button>
							</div>
						))}

						{/* Add Section Trigger */}
						<button
							style={{
								background: 'none',
								border: 'none',
								color: '#147B58',
								fontWeight: '500',
								fontSize: '15px',
								cursor: 'pointer',
								alignSelf: 'flex-start',
								padding: 0,
							}}
							onClick={handleAddSection}
						>
							Add Section +
						</button>
					</div>
				)}
			</div>

			{/* Bottom Navigation */}
			<div className={styles.footer}>
				{activeTabIndex > 0 && (
					<button className={styles.footerBack} onClick={handleBack}>
						Back
					</button>
				)}
				<button
					className={styles.footerNext}
					onClick={() => {
						const isValid = validateStep();
						if (!isValid) return;

						activeTabIndex >= 2 ? handlePublish() : handleNext();
					}}
					// disabled={isNextDisabled()}
				>
					{activeTabIndex >= 2 ? 'Publish Program' : 'Next'}
				</button>
			</div>

			<ResponsiveModal
				open={successModal.open}
				onClose={() =>
					setSuccessModal(prev => ({ ...prev, open: false }))
				}
			>
				<SuccessModal
					title={successModal.title}
					subtitle={successModal.subtitle}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={errorModal.open}
				onClose={() => setErrorModal({ open: false, message: '' })}
			>
				<ErrorPopupModal
					title={errorModal.message}
					onCancel={() => setErrorModal({ open: false, message: '' })}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={isAddSolutionOpen}
				onClose={() => setIsAddSolutionOpen(false)}
			>
				<AddSolution
					onOk={handleCreateSolution}
					onCancel={() => setIsAddSolutionOpen(false)}
				/>
			</ResponsiveModal>
		</div>
	);
};

export default ProgramForm;
